import React from 'react';
import { requestAccessUrl } from '../config/links';

/* The three pricing cards + the node-definition footnote, shared by the
   homepage pricing section and the standalone /pricing page. Workbench
   style: paper plan cards, dark Enterprise, burnt-orange Founding Partner.
   Explicit values (not scoped vars) so the cards read identically on
   notebook-styled subpages. Each CTA links to the app's request-access
   flow with its tier context. */

const serif = "Georgia, 'Times New Roman', serif";
const sans = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const mono = "ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, Consolas, monospace";

const tagStyle = {
  display: 'inline-flex', padding: '5px 9px', borderRadius: 999, fontFamily: mono,
  fontSize: 'var(--t-sm)', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
  alignSelf: 'flex-start', marginBottom: 16,
};
const planBase = { borderRadius: 16, padding: 26, display: 'flex', flexDirection: 'column', fontFamily: sans };
const h3Style = { fontFamily: serif, fontWeight: 500, fontSize: 'var(--t-md)', margin: '0 0 6px' };
const priceStyle = { fontFamily: serif, fontSize: 'var(--t-lg)', letterSpacing: '-0.02em', lineHeight: 1, margin: '6px 0 2px' };
const priceSub = { fontFamily: mono, fontSize: 'var(--t-sm)', letterSpacing: '0.06em', display: 'block', marginTop: 8, textTransform: 'uppercase' };
const listStyle = { listStyle: 'none', margin: '0 0 22px', padding: 0, display: 'grid', gap: 9, flex: 1 };
const btnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '13px 22px',
  borderRadius: 999, fontFamily: sans, fontWeight: 700, fontSize: 'var(--t-md)', textDecoration: 'none',
};

const Li = ({ children, color = 'var(--wb-ink-2)', check = 'var(--wb-green)' }) => (
  <li style={{ fontSize: 'var(--t-md)', lineHeight: 1.45, color, paddingLeft: 20, position: 'relative' }}>
    <span style={{ position: 'absolute', left: 0, fontFamily: mono, fontSize: 'var(--t-sm)', color: check }}>✓</span>
    {children}
  </li>
);

const PricingCards = () => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] items-stretch">
      {/* Team — free for the first 100 nodes, then $250/user/mo up to 2,500 nodes */}
      <div style={{ ...planBase, background: 'var(--wb-paper)', border: '2px solid var(--wb-orange)' }}>
        <span style={{ ...tagStyle, background: 'var(--wb-green-wash)', color: 'var(--wb-green)' }}>Start free</span>
        <h3 style={h3Style}>Team</h3>
        <div style={priceStyle}>
          Free
          <small style={{ ...priceSub, color: 'var(--wb-muted)' }}>Your first 100 nodes</small>
        </div>
        <p style={{ fontSize: 'var(--t-md)', color: 'var(--wb-muted)', margin: '10px 0 18px' }}>
          then <b style={{ color: 'var(--wb-ink)' }}>$250</b>/user/mo · up to 2,500 nodes
        </p>
        <ul style={listStyle}>
          {[
            'Private projects',
            'Full engineering lifecycle + evidence graph',
            'Advanced AI engineering intelligence',
            'Carry-forward learning',
            'DFM + quote workflows',
            'Team administration',
            'Priority support',
          ].map((item) => <Li key={item}>{item}</Li>)}
        </ul>
        <div style={{ border: '1px solid var(--wb-orange)', background: 'var(--wb-wash)', borderRadius: 10, padding: '12px 14px', margin: '0 0 18px' }}>
          <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--wb-orange)', fontWeight: 800 }}>Startup credit</div>
          <div style={{ fontFamily: serif, fontSize: 'var(--t-md)', margin: '3px 0 2px', color: 'var(--wb-ink)' }}>
            $50<span style={{ fontSize: 'var(--t-sm)', color: 'var(--wb-muted)' }}>/user/mo</span>
          </div>
          <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', lineHeight: 1.45 }}>
            Solo founders and qualified teams under five engineers. Free for university teams with a .edu email.
          </p>
        </div>
        <a href={requestAccessUrl('Team')} style={{ ...btnStyle, background: 'var(--wb-ink)', color: 'var(--wb-on-ink)', border: '1px solid var(--wb-ink)' }}>
          Request free access
        </a>
      </div>

      {/* Enterprise — custom */}
      <div style={{ ...planBase, background: 'var(--wb-raised)', color: '#f5f0e7', border: '1px solid var(--wb-raised-line)' }}>
        <span style={{ ...tagStyle, background: 'var(--wb-raised-line)', color: '#c9c4ba' }}>Custom</span>
        <h3 style={h3Style}>Enterprise</h3>
        <div style={priceStyle}>
          Custom
          <small style={{ ...priceSub, color: '#8f8a80' }}>For larger organizations</small>
        </div>
        <p style={{ fontSize: 'var(--t-md)', color: '#aaa49a', margin: '10px 0 18px' }}>
          Everything in Team, <b style={{ color: '#f5f0e7' }}>plus</b>:
        </p>
        <ul style={listStyle}>
          {[
            'SSO + security reviews',
            'Deployment controls',
            'ITAR-aligned deployment — bring your own local AI',
            'Integrations',
            'Onboarding + dedicated support',
          ].map((item) => <Li key={item} color="#c9c4ba" check="#d98a5c">{item}</Li>)}
        </ul>
        <a href={requestAccessUrl('Enterprise')} style={{ ...btnStyle, background: '#b94f1e', color: '#fff', border: '1px solid #b94f1e' }}>
          Contact sales
        </a>
      </div>

      {/* Founding Partner — one-time, burnt-orange premium treatment */}
      <div style={{ ...planBase, background: 'linear-gradient(160deg, #c8541f, #a23c14)', color: '#fdf6ec', border: '1px solid #8d3512' }}>
        <span style={{ ...tagStyle, background: 'rgba(253,246,236,0.18)', color: '#fdf6ec' }}>★ Limited</span>
        <h3 style={h3Style}>Founding Partner</h3>
        <div style={priceStyle}>
          $25,000
          <small style={{ ...priceSub, color: '#f4cdb2' }}>One-time · founding design partner</small>
        </div>
        <p style={{ fontSize: 'var(--t-md)', color: '#f4cdb2', margin: '10px 0 18px' }}>
          Partner directly with the team <b style={{ color: '#fff' }}>building Factory OS</b>.
        </p>
        <ul style={listStyle}>
          {[
            'Five seats',
            'Priority roadmap access',
            'Direct product collaboration',
            'Locked pricing',
            'Early feature access',
            'Recognition as a founding design partner',
          ].map((item) => <Li key={item} color="#fbe4d2" check="#ffd9b8">{item}</Li>)}
        </ul>
        <a href={requestAccessUrl('Founding Partner')} style={{ ...btnStyle, background: '#fdf6ec', color: '#a23c14', border: '1px solid #fdf6ec' }}>
          Become a partner
        </a>
      </div>
    </div>

    {/* Footer note — what a node is + the free promise */}
    <div className="mt-7 max-w-[860px] mx-auto text-center">
      <p style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-muted)', lineHeight: 1.6, margin: 0 }}>
        A <b style={{ color: 'var(--wb-ink)' }}>node</b> is a part, sub-assembly, assembly, or product — with its full engineering record attached.
        Revisions are unlimited and never count against your total. Your first <b style={{ color: 'var(--wb-ink)' }}>100 nodes</b> are free.
        At 100, your record goes read-only until you pick a plan — <b style={{ color: 'var(--wb-ink)' }}>nothing is ever deleted</b>.
        Team scales to <b style={{ color: 'var(--wb-ink)' }}>2,500 nodes</b> — we never charge per node; grow past that and you&apos;ve reached Enterprise scale, and we&apos;ll move you over.
      </p>
      <p className="mt-2" style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-faint)', letterSpacing: '0.03em', margin: '8px 0 0' }}>
        Questions about nodes, qualification, or the free tier? <a href="/faq" style={{ color: 'var(--wb-orange)' }}>Read the FAQ</a>
      </p>
    </div>
  </>
);

export default PricingCards;
